import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  Link,
  SearchInput,
  H3,
  Filter,
} from 'next/components/ui';
import Pagination from 'components/Common/UI/Pagination/Pagination';
import Loader from 'components/Common/Loader';
import NoData from 'assets/images/no-data.svg';
import NoResults from 'assets/images/no-results.svg';
import {
  ProfilePanel,
  PanelHeader,
  TableWrapper,
  PaginationInformation,
  LoaderWrapper,
  NoResultsMessage,
  NoResultsMessageEmphasis,
  Footer,
  Subtitle,
  PanelTitle,
} from 'components/Users/Profile/styled';
import { DEFAULT_PAGE_SIZE } from 'components/Users/Profile/constants';
import useFilters from 'next/hooks/use-filter';
import {
  callApi as fetchUserQualifiedContent,
  remove,
} from 'entities/user-qualified-content';
import { selectUserQualifiedContentFlat } from 'selectors/user-qualified-content';
import {
  EligibilityTable,
  EligibilityRow,
  EligibleIcon,
  FiltersWrapper,
  EligibilityLabel,
  ActivityCell,
} from './styled';
import CriteriaModalManager from './CriteriaModalManager';
import { filterConditions, evaluateConditions } from './utils';

const sortByEligibility = (a, b) => {
  if (a.eligible && !b.eligible) return -1;
  if (!a.eligible && !b.eligible) return 1;
  return 0;
};

const getActivityLink = (activity, id) => {
  switch (activity) {
    case 'checklists':
      return `/checklists/${id}/analytics`;
    case 'satisfaction_survey':
      return `/nps`;
    case 'journey':
    default:
      return `/journeys/${id}/analytics`;
  }
};

export function Eligibility({
  userQualifiedContent,
  pageSize = DEFAULT_PAGE_SIZE,
  onLoad,
  onUnload,
}) {
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const isLoading = !userQualifiedContent;

  const { filters, handleFilterChange, handleFilterClear, filterFunction } =
    useFilters({
      eligibility: {
        name: 'Eligibility',
        options: {
          eligible: {
            label: (
              <EligibilityLabel>
                Eligible <EligibleIcon $eligible icon="check-circle" />
              </EligibilityLabel>
            ),
            enabled: false,
            filterFn: item => item.eligible,
            checkboxProps: {
              'aria-label': 'filter by eligible',
            },
          },
          ineligible: {
            label: (
              <EligibilityLabel>
                Ineligible <EligibleIcon $eligible={false} icon="ban" />
              </EligibilityLabel>
            ),
            enabled: false,
            filterFn: item => !item.eligible,
            checkboxProps: {
              'aria-label': 'filter by ineligible',
            },
          },
        },
      },
      activity: {
        name: 'Activity',
        options: {
          flows: {
            label: 'Flow',
            enabled: false,
            filterFn: item => item.activity.toLowerCase() === 'flow',
          },
          checklists: {
            label: 'Checklist',
            enabled: false,
            filterFn: item => item.activity.toLowerCase() === 'checklists',
          },
        },
      },
    });

  useEffect(() => {
    setCurrentPage(0);
  }, [filters, query]);

  useEffect(() => {
    onLoad?.();
    return () => {
      onUnload?.();
    };
  }, [onLoad]);

  const filteredQualifiedContent = userQualifiedContent
    ?.map(rule => ({
      eligible: evaluateConditions(filterConditions(rule.conditions)),
      ...rule,
    }))
    ?.filter(
      ({ name }) => name && name.toUpperCase().includes(query.toUpperCase())
    )
    .filter(filterFunction);

  const qualifiedContentSlice = filteredQualifiedContent
    ?.sort(sortByEligibility)
    .slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const hasNoData = userQualifiedContent?.length === 0;
  const hasNoResults =
    !isLoading && !hasNoData && qualifiedContentSlice.length === 0;

  const onChangePage = page => {
    setCurrentPage(page);
  };

  return (
    <ProfilePanel>
      <PanelHeader>
        <PanelTitle>
          <H3>Eligibility</H3>
          <Subtitle>
            Flows and Checklists the given user should be able to see (highest
            priority at the top)
          </Subtitle>
        </PanelTitle>
        <FiltersWrapper>
          <SearchInput
            placeholder="Search for Experience name"
            value={query}
            onChange={({ target: { value } }) => {
              setQuery(value);
            }}
          />
          <Filter
            filters={filters}
            onChange={handleFilterChange}
            onClear={handleFilterClear}
          />
        </FiltersWrapper>
      </PanelHeader>
      <TableWrapper>
        <EligibilityTable>
          <Thead>
            <Tr>
              <Th>Experience name</Th>
              <Th>Experience</Th>
              <Th>Eligibility</Th>
            </Tr>
          </Thead>
          <Tbody>
            {qualifiedContentSlice?.map(
              ({ name, activity, eligible, id, conditions }) => {
                return (
                  <EligibilityRow key={name}>
                    <Td title={name}>
                      <Link to={getActivityLink(activity, id)} target="_blank">
                        {name}
                      </Link>
                    </Td>
                    <ActivityCell title={activity}>{activity}</ActivityCell>
                    <Td>
                      <EligibleIcon
                        $eligible={eligible}
                        icon={eligible ? 'check-circle' : 'ban'}
                      />
                      {eligible ? (
                        'Eligible'
                      ) : (
                        <CriteriaModalManager
                          name={name}
                          conditions={conditions}
                        />
                      )}
                    </Td>
                  </EligibilityRow>
                );
              }
            )}
          </Tbody>
        </EligibilityTable>
        {isLoading && (
          <LoaderWrapper>
            <Loader margin="1em" />
          </LoaderWrapper>
        )}
        {hasNoResults && (
          <NoResultsMessage>
            <img src={NoResults} alt="No data found" />
            <NoResultsMessageEmphasis>
              No results found
            </NoResultsMessageEmphasis>
            Try adjusting your filters for results
          </NoResultsMessage>
        )}
        {hasNoData && (
          <NoResultsMessage>
            <img src={NoData} alt="No data found" />
            We don’t have data for you yet
          </NoResultsMessage>
        )}
      </TableWrapper>
      <Footer>
        {!isLoading && qualifiedContentSlice.length > 0 && (
          <>
            <Pagination
              pageCount={Math.ceil(filteredQualifiedContent?.length / pageSize)}
              currentIndex={currentPage}
              onChange={onChangePage}
            />

            <PaginationInformation
              currentIndex={currentPage}
              pageSize={pageSize}
              total={filteredQualifiedContent?.length}
            />
          </>
        )}
      </Footer>
    </ProfilePanel>
  );
}

Eligibility.propTypes = {
  userQualifiedContent: PropTypes.arrayOf(PropTypes.object),
  pageSize: PropTypes.number,
  onUnload: PropTypes.func,
  onLoad: PropTypes.func,
};

const mapStateToProps = (state, { userId }) => ({
  userQualifiedContent: selectUserQualifiedContentFlat(state, userId),
});

const mapDispatchToProps = (dispatch, { userId }) => ({
  onLoad: () => dispatch(fetchUserQualifiedContent({ userId })),
  onUnload: () => dispatch(remove(userId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Eligibility);
